import type { FormActions, FormColumn, FormField } from '@core-engine/schema/types.js'
import { buildColumnSize, getTableColumnSize } from '@next-adapter/generators/table-column-size.js'

// ============================================================================
// Column Definition Helper
// ============================================================================

function generateColumnDef(col: FormColumn): string {
  const sortableHeader = col.sortable
    ? `header: ({ column }) => {
      const sortDirection = column.getIsSorted()

      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-full min-w-0 justify-start overflow-hidden px-0! border-none rounded-none bg-transparent! scale-100! group"
        >
          <span className="truncate">${col.header}</span>
          <SortIndicator direction={sortDirection} />
        </Button>
      )
    }`
    : `header: '${col.header}'`

  let cellDef: string
  switch (col.type) {
    case 'email':
      cellDef = `cell: ({ row }) => {
      const email = row.getValue('${col.accessorKey}') as string
      return <div className="text-sm font-medium">{email || '-'}</div>
    }`
      break
    case 'date':
      cellDef = `cell: ({ row }) => {
      const date = row.getValue('${col.accessorKey}') as string
      return date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'
    }`
      break
    case 'badge':
      cellDef = `cell: ({ row }) => {
      const value = row.getValue('${col.accessorKey}') as string
      return <Badge variant="outline">{value || 'N/A'}</Badge>
    }`
      break
    case 'number':
      cellDef = `cell: ({ row }) => {
      const value = row.getValue('${col.accessorKey}') as number
      return <div className="text-right">{value !== null && value !== undefined ? value : '-'}</div>
    }`
      break
    default:
      cellDef = `cell: ({ row }) => {
      const value = row.getValue('${col.accessorKey}') as string
      return <div>{value || '-'}</div>
    }`
  }

  return `  {
    accessorKey: '${col.accessorKey}',
    ${buildColumnSize(col.size)}${sortableHeader},
    ${cellDef}
  }`
}

// ============================================================================
// Columns Generator
// ============================================================================

export function generateColumns(
  fields: FormField[],
  pascal: string,
  _kebab: string,
  _camel: string,
  actionImportPath: string,
  customColumns?: FormColumn[],
  actions?: FormActions
): string {
  const hasBadge = customColumns?.some((c) => c.type === 'badge') ?? false
  const hasDelete = actions?.delete !== false

  const columnDefs = customColumns
    ? customColumns.map((col) => generateColumnDef(col)).join(',\n')
    : fields
        .filter((f) => f.name)
        .map((f) => {
          const accessor = f.name!
          return `  {
    accessorKey: '${accessor}',
    size: ${getTableColumnSize('default')},
    header: ({ column }) => {
      const sortDirection = column.getIsSorted()

      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-full min-w-0 justify-start overflow-hidden px-0! border-none rounded-none bg-transparent! scale-100! group"
        >
          <span className="truncate">${f.label}</span>
          <SortIndicator direction={sortDirection} />
        </Button>
      )
    },
    cell: ({ row }) => {
      const value = row.getValue('${accessor}')
      return <div>${f.type === 'textarea' ? "{value ? String(value).substring(0, 100) + (String(value).length > 100 ? '...' : '') : '-'}" : "{String(value ?? '-')}"}</div>
    }
  }`
        })
        .join(',\n')

  const actionsImport = hasDelete ? `import { ${pascal}Actions } from './actions'\n` : ''
  const actionsColumn = hasDelete
    ? `  {
    id: 'actions',
    size: 64,
    enableResizing: false,
    meta: { width: '64px' },
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <${pascal}Actions id={row.original.id} />
      </div>
    )
  }`
    : ''

  return `'use client'

import { SortIndicator } from '@admin/components/shared/sort-indicator'
import type { ${pascal}SubmissionData } from '@admin/actions/${actionImportPath}'
${actionsImport}${hasBadge ? "import { Badge } from '@admin/components/ui/badge'\n" : ''}import { Button } from '@admin/components/ui/button'
import { Checkbox } from '@admin/components/ui/checkbox'
import type { ColumnDef } from '@tanstack/react-table'

export const columns: ColumnDef<${pascal}SubmissionData>[] = [
  {
    id: 'select',
    size: 50,
    enableResizing: false,
    meta: { width: '50px' },
    header: ({ table }) => (
      <Checkbox
        checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
        onCheckedChange={(value: boolean) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false
  },
${columnDefs ? `${columnDefs}${actionsColumn ? ',' : ''}` : ''}${actionsColumn ? `\n${actionsColumn}` : ''}
]
`
}
